import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import apiV2 from "api-v2";

import type { ResourceCapabilityResource } from "newui/features/Rfx/Evaluation/types/eventGrant.type";

/**
 * `useResourceCapability(context_type, context_id)` — the SECOND-shape
 * capability hook (FE-3/H1). Fed by `GET /api/v2/rbac/me/permissions/resource`,
 * it returns the caller's resolved capability on ONE resource context via the
 * active event grant / scope grant / own tenant.
 *
 * Why a second shape: a flat module→capability map cannot represent "full on
 * the RFx module in general, but only panel-member capabilities on RFx-B
 * specifically". `PanelInviteModal`/`CoiResolutionModal` and ALL per-RFx gating
 * consume THIS hook — never the global `usePermissions`. Capability is
 * server-authoritative (BE-6/C8): the FE never derives event-role capability.
 *
 * The response is per-user + resource-scoped, so it is cached privately and
 * short-lived (mirrors the endpoint's `Cache-Control: private, no-store`).
 */

export const resourceCapabilityKeys = {
	all: ["rbac", "resource-capability"] as const,
	one: (contextType: string, contextId: number | null | undefined) =>
		[...resourceCapabilityKeys.all, contextType, contextId ?? null] as const,
};

export const fetchResourceCapability = async (
	contextType: string,
	contextId: number
): Promise<ResourceCapabilityResource> => {
	const response = await apiV2.get(`/rbac/me/permissions/resource`, {
		params: { context_type: contextType, context_id: contextId },
	});
	return response.data.data as ResourceCapabilityResource;
};

export const useResourceCapability = (
	contextType: string,
	contextId: number | null | undefined
): UseQueryResult<ResourceCapabilityResource> => {
	return useQuery({
		queryKey: resourceCapabilityKeys.one(contextType, contextId),
		queryFn: () => fetchResourceCapability(contextType, contextId as number),
		enabled: Boolean(contextType) && contextId != null,
		// Capability is security state — do not serve stale grants after a
		// revoke/close. Keep it fresh and never cache to disk.
		staleTime: 0,
		gcTime: 0,
	});
};
